import { ErrorOutline as IconConditionalDisplay, Edit as IconEdit } from "@mui/icons-material";
import { Box, Chip, IconButton, Stack } from "@mui/material";
import { useTranslation } from "react-i18next";

import { Shared_FormTemplate_QuestionFragment } from "~/shared/components/FormTemplate/fragments/fragments.generated";
import QuestionAmount from "~/shared/components/FormTemplate/questions/QuestionAmount";
import QuestionDateTime from "~/shared/components/FormTemplate/questions/QuestionDateTime";
import QuestionDropdown from "~/shared/components/FormTemplate/questions/QuestionDropdown";
import QuestionSelect from "~/shared/components/FormTemplate/questions/QuestionSelect";
import QuestionText from "~/shared/components/FormTemplate/questions/QuestionText";
import { NarrowedFormField } from "~/shared/components/FormTemplate/types";
import { splitGqlUnion } from "~/shared/helpers/splitGqlUnion";

type Props = {
    currentQuestion: Shared_FormTemplate_QuestionFragment;
    onEditClick: () => void;
};

export default function QuestionItemPreview({ currentQuestion, onEditClick }: Props) {
    const { t } = useTranslation("formTemplateEditor");

    const hasCondition = currentQuestion.condition.__typename !== "GqlSettingsFormTemplateQuestionConditionNone";

    return (
        <Stack direction="column" rowGap={1}>
            <Stack direction="row" columnGap={1}>
                {hasCondition && (
                    <Chip
                        icon={<IconConditionalDisplay />}
                        label={t("formTemplateQuestionForm.chipConditionalDisplay")}
                        color="secondary"
                    />
                )}
                {currentQuestion.tags?.map(tag => (
                    <Chip key={tag} label={t(`formTemplateQuestionForm.${tag}TagLabel`)} />
                ))}
            </Stack>
            <Stack direction="row" columnGap={2} justifyContent="space-between" alignItems="center">
                {splitGqlUnion(currentQuestion.type, {
                    GqlSettingsFormTemplateQuestionTypeSelect: t => (
                        <QuestionSelect field={NarrowedFormField.create(currentQuestion, t)} />
                    ),
                    GqlSettingsFormTemplateQuestionTypeDropdown: t => (
                        <QuestionDropdown field={NarrowedFormField.create(currentQuestion, t)} />
                    ),
                    GqlSettingsFormTemplateQuestionTypeAmount: t => (
                        <Box flex={1}>
                            <QuestionAmount field={NarrowedFormField.create(currentQuestion, t)} disabled />
                        </Box>
                    ),
                    GqlSettingsFormTemplateQuestionTypeRecording: () => "Recording field",
                    GqlSettingsFormTemplateQuestionTypeText: t => (
                        <QuestionText field={NarrowedFormField.create(currentQuestion, t)} />
                    ),
                    GqlSettingsFormTemplateQuestionTypeDateTime: t => (
                        <QuestionDateTime field={NarrowedFormField.create(currentQuestion, t)} disabled />
                    ),
                })}
                <IconButton onClick={onEditClick}>
                    <IconEdit />
                </IconButton>
            </Stack>
        </Stack>
    );
}
